import type {RefObject} from 'react'

import {InfoTooltip} from 'src/common/InfoTooltip'
import {INPUT_CLASS} from '../constants'
import type {Country, NlMunicipality} from '../types'
import {toISOLocal} from '../utils'
import {DateTimePicker} from './DateTimePicker'

interface TripDetailsFormProps {
  country: Country | null
  departure: string
  returnDate: string
  priorDays: number
  atDistance: number
  nlMunicipality: NlMunicipality
  warning: string
  warningRef: RefObject<HTMLDivElement>
  onDepartureChange: (value: string) => void
  onReturnChange: (value: string) => void
  onPriorDaysChange: (value: number) => void
  onAtDistanceChange: (value: number) => void
  onNlMunicipalityChange: (value: NlMunicipality) => void
}

export const TripDetailsForm = ({
  country,
  departure,
  returnDate,
  priorDays,
  atDistance,
  nlMunicipality,
  warning,
  warningRef,
  onDepartureChange,
  onReturnChange,
  onPriorDaysChange,
  onAtDistanceChange,
  onNlMunicipalityChange,
}: TripDetailsFormProps) => (
  <div>
    <div className="px-6 py-8">
      <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-grey-450">Travel dates</p>
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
        <DateTimePicker
          label="Departure"
          value={departure ? new Date(departure) : null}
          onChange={(date) => onDepartureChange(toISOLocal(date))}
          hasError={warning.includes('valid dates')}
        />
        <DateTimePicker
          label="Return"
          value={returnDate ? new Date(returnDate) : null}
          onChange={(date) => onReturnChange(toISOLocal(date))}
          minDate={departure ? new Date(departure) : null}
          hasError={warning.includes('after departure') || warning.includes('valid dates')}
        />
      </div>

      {country === 'de' && (
        <div className="mt-4 rounded-lg bg-[#f7f6f3] p-5">
          <div className="mb-2 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-grey-450">
            Three-month rule
            <InfoTooltip id="three-month-rule">
              Tax-free per diem is limited to three months at the same work location (§9 Abs. 4a EStG). After three
              months, the allowance drops to zero. A 4-week break at that location resets the counter.
            </InfoTooltip>
          </div>
          <p className="mb-3 text-13 text-moss-black">
            How many days has the employee previously spent in Germany on this assignment?
          </p>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={0}
              value={priorDays}
              onChange={(e) => onPriorDaysChange(Number(e.target.value))}
              className={`${INPUT_CLASS} max-w-[80px]`}
            />
            <span className="text-13 font-medium text-grey-450">days</span>
          </div>
          <p className="mt-2 text-12 text-grey-450">Used to determine whether the Dreimonatsfrist reduction applies</p>
        </div>
      )}

      {country === 'at' && (
        <div className="mt-4 rounded-lg bg-[#f7f6f3] p-5">
          <div className="mb-2 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-grey-450">
            Distance rule
            <InfoTooltip id="distance-rule">
              Minimum 25 km required for any per diem. Under 120 km: limited to 5 consecutive days or 15 days per year
              at the same domestic location. Over 120 km: tax-free for up to 6 months.
            </InfoTooltip>
          </div>
          <p className="mb-3 text-13 text-moss-black">How far is the destination from the employee's workplace?</p>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={0}
              value={atDistance}
              onChange={(e) => onAtDistanceChange(Number(e.target.value))}
              className={`${INPUT_CLASS} max-w-[80px]`}
            />
            <span className="text-13 font-medium text-grey-450">km</span>
          </div>
          <p className="mt-2 text-12 text-grey-450">Minimum 25 km required for per diem eligibility</p>
        </div>
      )}

      {country === 'nl' && (
        <div className="mt-4 rounded-lg bg-[#f7f6f3] p-5">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-grey-450">Municipality rule</p>
          <p className="mb-3 text-13 text-moss-black">
            Is the destination in a different municipality or at least 1 km from the workplace?
          </p>
          <div className="space-y-2">
            <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-grey-150 bg-moss-white px-4 py-3">
              <input
                type="radio"
                name="nl-municipality"
                checked={nlMunicipality === 'yes'}
                onChange={() => onNlMunicipalityChange('yes')}
                className="size-4 accent-green-dark"
              />
              <span className="text-13 text-moss-black">Different municipality / 1+ km away</span>
            </label>
            <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-grey-150 bg-moss-white px-4 py-3">
              <input
                type="radio"
                name="nl-municipality"
                checked={nlMunicipality === 'no'}
                onChange={() => onNlMunicipalityChange('no')}
                className="size-4 accent-green-dark"
              />
              <span className="text-13 text-moss-black">Same municipality (not eligible)</span>
            </label>
          </div>
          {nlMunicipality === 'no' && (
            <div className="mt-3 flex items-center gap-2 rounded-lg border border-ui-red-150 bg-ui-red-150 px-4 py-3 text-13 text-ui-red-700">
              <span>Per diem is not eligible when the destination is in the same municipality.</span>
            </div>
          )}
          <p className="mt-2 text-12 text-grey-450">Required for WKR per diem eligibility</p>
        </div>
      )}

      {warning && (
        <div
          ref={warningRef}
          className="mt-3 flex items-center gap-2 rounded-lg border border-ui-red-150 bg-ui-red-150 px-4 py-3 text-13 text-ui-red-700"
        >
          <span>{warning}</span>
        </div>
      )}
    </div>
  </div>
)
